import { getDisplayCategories } from 'academy-common-lib/utils/categories';
import { useLearningContentCardTo } from '../cardsHooks';
// @ts-expect-error Could not find a declaration file for module
import useFilterParams from '../../../hooks/useFilterParams';
import UsageTracker from '../../../utils/UsageTracker';
import LearningContentCardAwardLink from './LearningContentCardAwardLink';
import CardMenuIcon from '../../cardMenu/components/CardMenuIcon';
import { useCallback, useEffect, useState } from 'react';
import { ThumbnailTypeMap } from 'academy-common-lib/features/cards/constants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/transitional/components/View';
import { EmbeddedVideo } from '../../video';
import ACContentCardLarge from 'academy-common-lib/components/card/ACContentCardLarge';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VIDEO_HOVER_TIMEOUT = 0;
const OnHoverVideoPlayerWrapper = styled.div.withConfig({
  displayName: "StandardLearningContentCard__OnHoverVideoPlayerWrapper"
})(["block-size:100%;inline-size:100%;inset-block-start:0;inset-inline-start:0;position:absolute;opacity:", ";transition:opacity 0.3s ease-in-out;"], ({
  visible
}) => visible ? '1' : '0');
const OnHoverVideoPlayer = ({
  item,
  isHovered
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [shouldStartPlaying, setShouldStartPlaying] = useState(false);
  useEffect(() => {
    let timeoutId;
    setIsVideoPlaying(false);
    if (isHovered) {
      timeoutId = setTimeout(() => {
        setShouldStartPlaying(true);
      }, VIDEO_HOVER_TIMEOUT);
    } else {
      setShouldStartPlaying(false);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isHovered]);
  if (item.type === 'LEARNING_PATH' || !isHovered || !shouldStartPlaying) {
    return null;
  }
  const handleTimeUpdate = seconds => {
    sessionStorage.setItem(JSON.stringify({
      id: item.id,
      type: item.type,
      language: item.language
    }), String(seconds));
  };
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(OnHoverVideoPlayerWrapper, {
      visible: isVideoPlaying,
      children: /*#__PURE__*/_jsx(EmbeddedVideo, {
        autoPlay: true,
        fallbackThumbnailUrl: item.previewThumbnailRectangleUrl,
        language: String(item.language || 'en').toLowerCase(),
        lessonId: item.type === 'LESSON' ? item.id.toString() : undefined,
        mutedByDefault: true,
        onPlay: handleVideoPlay,
        onTimeUpdate: handleTimeUpdate,
        playerType: "compactPlayerWithCustomMenu",
        renderContext: "academy-play-on-hover",
        shortId: item.type === 'SHORT_FORM_CONTENT' ? item.id : undefined,
        startTime: Number(sessionStorage.getItem(JSON.stringify({
          id: item.id,
          type: item.type,
          language: item.language
        }))),
        trackId: item.type === 'TRACK' ? item.id : undefined
      })
    }), /*#__PURE__*/_jsx(View, {
      blockSize: "79%",
      insetBlockStart: "0",
      insetInlineEnd: "60px",
      insetInlineStart: "0",
      position: "absolute",
      visibility: isVideoPlaying ? 'visible' : 'hidden',
      zIndex: "50"
    })]
  });
};
export const StandardLearningContentCard = ({
  item,
  onItemClick,
  onItemMenuClick,
  showOverview,
  isGhost,
  position,
  overviewType,
  playlistId,
  renderMenu = menu => menu,
  extraTrackingInfo,
  isNotFilterable,
  disableLink,
  flush,
  body,
  footer,
  thumbnailOverlay,
  ribbonText,
  ribbonUse,
  menuAlwaysVisible = false,
  isInternalContent,
  selected
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    id,
    type,
    language,
    name,
    progress,
    previewThumbnailRectangleUrl
  } = item;
  const {
    certification,
    subCategories,
    duration
  } = item;
  const award = progress === null || progress === void 0 ? void 0 : progress.award;
  const displayCategories = subCategories ? getDisplayCategories(subCategories) : [];
  const completed = (progress === null || progress === void 0 ? void 0 : progress.status) === 'COMPLETED';
  const {
    itemsCount = 0
  } = item;
  const {
    completedItemsCount = 0
  } = progress || {};
  const to = useLearningContentCardTo({
    item,
    overviewType,
    playlistId,
    showOverview
  });
  const filterParams = useFilterParams();
  const handleOnClick = useCallback(() => {
    if (!disableLink) UsageTracker.track('interaction', Object.assign({
      action: 'content-card-click',
      contentId: id,
      contentType: type,
      appearsIn: overviewType,
      position,
      name,
      filters: JSON.stringify(filterParams),
      style: 'default',
      isInProgress: (progress === null || progress === void 0 ? void 0 : progress.status) === 'IN_PROGRESS',
      isCertification: certification,
      playlistId
    }, extraTrackingInfo));
    if (onItemClick) onItemClick(item);
  }, [disableLink, id, type, overviewType, position, name, filterParams, progress === null || progress === void 0 ? void 0 : progress.status, certification, playlistId, extraTrackingInfo, onItemClick, item]);
  const getProgress = () => {
    if (completed) return duration || itemsCount;
    if (type === 'PLAYLIST' || type === 'LEARNING_PATH') {
      return completedItemsCount;
    }
    return (duration || itemsCount) * ((progress === null || progress === void 0 ? void 0 : progress.completedPercentage) || 0) / 100;
  };
  return /*#__PURE__*/_jsx(ACContentCardLarge, {
    awardLink: /*#__PURE__*/_jsx(LearningContentCardAwardLink, {
      award: award,
      use: "on-light"
    }),
    cardMenu: renderMenu( /*#__PURE__*/_jsx(CardMenuIcon, {
      item: {
        id,
        type: type,
        language: language
      },
      onMenuIconClick: () => onItemMenuClick === null || onItemMenuClick === void 0 ? void 0 : onItemMenuClick(item)
    })),
    contentHref: to.fullUrl,
    contentType: certification ? 'CERTIFICATION' : ThumbnailTypeMap[type],
    "data-test-id": "learning-content-card",
    "data-test-is-certification": Boolean(certification),
    "data-test-is-ghost": Boolean(isGhost),
    "data-test-is-unfilterable": Boolean(isNotFilterable),
    "data-test-meta": id,
    "data-test-type": type,
    description: body,
    flush: flush,
    footer: footer,
    imageSrc: previewThumbnailRectangleUrl,
    isGhost: isGhost,
    isInternalContent: isInternalContent,
    length: duration || itemsCount,
    lengthType: type === 'PLAYLIST' || type === 'LEARNING_PATH' ? 'items' : 'time',
    linkMode: "react-router",
    menuAlwaysVisible: menuAlwaysVisible,
    name: name,
    onLinkClick: handleOnClick,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    progress: getProgress(),
    ribbonText: ribbonText,
    ribbonUse: ribbonUse || 'beta',
    selected: selected,
    tags: displayCategories.map(categoryKey => /*#__PURE__*/_jsx(FormattedMessage, {
      message: `academyCommon.categories.${categoryKey}`
    }, categoryKey)),
    thumbnailOverlay: thumbnailOverlay,
    videoPlayer: /*#__PURE__*/_jsx(OnHoverVideoPlayer, {
      isHovered: isHovered,
      item: item
    })
  });
};
export default StandardLearningContentCard;